import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PriceTagPrint = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasPrinted = useRef(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (products.length > 0 && !hasPrinted.current) {
      hasPrinted.current = true;
      setTimeout(() => window.print(), 400);
    }
  }, [products]);

  const fetchProducts = async () => {
    try {
      const ids = searchParams.get('ids')?.split(',').filter(Boolean) || [];
      if (ids.length === 0) {
        toast.error('No products selected');
        navigate('/menu-products');
        return;
      }
      const response = await axios.get(`${API}/menu-products`, { withCredentials: true });
      const filtered = response.data.filter(p => ids.includes(p.loyverse_id));
      setProducts(filtered);
    } catch {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      <style>{`
        @media print {
          @page {
            size: 80mm 28mm;
            margin: 0;
          }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
          }
          /* Hide sidebar, header, nav, and screen controls */
          .screen-controls,
          nav, aside, header, .no-print,
          [data-testid="sidebar"],
          [data-testid="mobile-menu-btn"] {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
            overflow: hidden !important;
          }
          /* Keep all wrapper elements visible but strip their layout styling */
          #root, #root > *, main, .min-h-screen, .flex-1,
          .min-h-screen > *, main > * {
            display: block !important;
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
            box-shadow: none !important;
            width: 100% !important;
            min-height: 0 !important;
            overflow: visible !important;
            background: white !important;
          }
          .price-tags-container {
            display: block !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .price-tag {
            width: 80mm;
            height: 28mm;
            page-break-after: always;
            break-after: page;
            overflow: hidden;
            box-sizing: border-box;
            padding: 1.5mm 2mm;
            display: flex !important;
            margin: 0;
          }
          .price-tag:last-child {
            page-break-after: auto;
            break-after: auto;
          }
        }

        .price-tag {
          width: 80mm;
          height: 28mm;
          display: flex;
          overflow: hidden;
          box-sizing: border-box;
          padding: 1.5mm 2mm;
          font-family: 'Arial Narrow', Arial, sans-serif;
          background: white;
          color: black;
        }
        .tag-left {
          width: 40mm;
          height: 25mm;
          display: flex;
          align-items: center;
          padding-right: 2mm;
          overflow: hidden;
        }
        .tag-left .product-name {
          font-weight: 700;
          line-height: 1.15;
          word-wrap: break-word;
          overflow-wrap: break-word;
          hyphens: auto;
        }
        .tag-right {
          width: 38mm;
          display: flex;
          flex-direction: column;
        }
        .tag-price-area {
          height: 18mm;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          padding-right: 1mm;
        }
        .tag-price {
          font-weight: 900;
          text-align: right;
          line-height: 1;
        }
        .tag-unit-area {
          height: 7mm;
        }
      `}</style>

      {/* Screen controls */}
      <div className="screen-controls p-6 space-y-4 max-w-2xl mx-auto" data-testid="price-tag-print-page">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Price Tags Preview</h1>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/menu-products')}
              className="px-4 py-2 border rounded-md hover:bg-gray-50"
            >
              Back
            </button>
            <button
              onClick={() => window.print()}
              className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800"
              data-testid="print-tags-btn"
            >
              Print {products.length} Tag{products.length !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500">
          Select your receipt printer (HPRT TP805L) in the print dialog. Set paper size to 80mm if available.
        </p>
      </div>

      {/* Price tags */}
      <div className="price-tags-container">
        <div className="screen-controls flex flex-col items-center gap-4 p-6">
          {products.map((product) => {
            const price = product.loyverse_price || 0;
            const name = product.name || '';
            // Dynamic font sizing for name based on length
            const nameFontSize = name.length > 40 ? '8pt' : name.length > 25 ? '9pt' : name.length > 15 ? '10pt' : '12pt';
            // Dynamic font sizing for price
            const priceStr = `R${price.toFixed(2)}`;
            const priceFontSize = priceStr.length > 8 ? '14pt' : priceStr.length > 6 ? '18pt' : '22pt';

            return (
              <div key={product.loyverse_id} className="price-tag" style={{ border: '1px dashed #ccc' }}>
                <div className="tag-left">
                  <div className="product-name" style={{ fontSize: nameFontSize }}>
                    {name}
                  </div>
                </div>
                <div className="tag-right">
                  <div className="tag-price-area">
                    <div className="tag-price" style={{ fontSize: priceFontSize }}>
                      {priceStr}
                    </div>
                  </div>
                  <div className="tag-unit-area" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Hidden print-only tags (without dashed border) */}
        {products.map((product) => {
          const price = product.loyverse_price || 0;
          const name = product.name || '';
          const nameFontSize = name.length > 40 ? '8pt' : name.length > 25 ? '9pt' : name.length > 15 ? '10pt' : '12pt';
          const priceStr = `R${price.toFixed(2)}`;
          const priceFontSize = priceStr.length > 8 ? '14pt' : priceStr.length > 6 ? '18pt' : '22pt';

          return (
            <div key={`print-${product.loyverse_id}`} className="price-tag" style={{ display: 'none' }}>
              <div className="tag-left">
                <div className="product-name" style={{ fontSize: nameFontSize }}>
                  {name}
                </div>
              </div>
              <div className="tag-right">
                <div className="tag-price-area">
                  <div className="tag-price" style={{ fontSize: priceFontSize }}>
                    {priceStr}
                  </div>
                </div>
                <div className="tag-unit-area" />
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default PriceTagPrint;
